import { Controller } from '@hotwired/stimulus';

// Wires the Compte prorata wizard's Card 1 (manager dropdown) to Card 2
// (contributors checkbox list). When the architect picks a manager, every
// contract owned by that manager's contractor is force-checked. Only the
// LAST remaining checked manager checkbox is locked (disabled) — the
// architect can still uncheck N-1 of N to exclude lots the gestionnaire
// holds but does not run as part of the compte prorata (spec § F7).
//
// Targets:
//   managerSelect          — the `<select>` storing `manager_contractor_id`.
//   contributorCheckbox    — every contract row's `<input type="checkbox">`,
//                            carrying `data-contractor-id` for matching.
//   managerRetentionSwitch — the Card 1 `<input type="checkbox" role="switch">`
//                            rendered by `Ui::SwitchComponent` (the data
//                            attributes land on the input itself, so `.checked`
//                            is readable here).
//   retentionWarning       — the Card 1 warning wrapper, stamped with the
//                            `data-contractor-id` its figures describe (they are
//                            server-rendered and never recomputed client-side).
//                            Present in the DOM ONLY
//                            when the manager has already been charged; the
//                            server renders its count + amount, never this file.
//
// On `connect()`, we only recompute the lock state from the SERVER-RENDERED
// initial checkbox state — we MUST NOT force-check manager checkboxes here,
// because on `/edit` the server has already rendered the persisted state
// (some manager lots may be intentionally unchecked because the architect
// excluded them in a prior save). Calling `onManagerChange()` from
// `connect()` would clobber that state and silently re-include the
// excluded lots. The force-check only belongs on a user-driven manager
// change (the `change->prorata-wizard#onManagerChange` action on the
// manager select).
//
// Server-side note: disabled checkboxes are not submitted by browsers. We
// dynamically insert a sibling `hidden_field_tag` next to each disabled
// manager row so the controller still receives the contract id in
// `included_contract_ids[]`. Enabled-but-unchecked manager checkboxes
// correctly omit themselves from the payload (and never carry the hidden
// mirror), which `ProrataAccountsController#account_params` treats as a
// user-intended partial exclusion. The model's
// `manager_contract_not_excluded` validation catches the pathological case
// where every manager contract is excluded.
//
// Encadré « Le changement vaut pour les prochaines situations »
// (`rateChangeNotice`, rendu seulement quand des contributions existent) :
// visible dès que le taux ou l'assiette saisis diffèrent des valeurs
// ENREGISTRÉES (`savedRate` / `savedBasis`, posées par le serveur), masqué
// sinon. Le serveur le rend déjà visible quand un 422 réaffiche un taux
// modifié : `connect()` rejoue la comparaison, il ne le masque pas d'office.
const HIDDEN_MIRROR_NAME = 'prorata_account[included_contract_ids][]';
const HIDDEN_MIRROR_FLAG = 'prorataMirrorFor';
const RATE_FIELD = 'prorata_account[retention_rate_percent]';
const BASIS_FIELD = 'prorata_account[retention_basis]';

// Vrai quand le taux ou l'assiette saisis diffèrent de ceux enregistrés.
// Le taux se compare en nombre (« 2 » = « 2.0 ») ; un champ vidé compte comme
// un changement, la validation serveur dira le reste.
export function rateOrBasisChanged({ savedRate, savedBasis, rate, basis }) {
  const typed = Number.parseFloat(String(rate).replace(',', '.'));
  const saved = Number.parseFloat(String(savedRate).replace(',', '.'));
  const rateChanged = Number.isNaN(typed) || typed !== saved;
  return rateChanged || basis !== savedBasis;
}

export default class extends Controller {
  static targets = [
    'managerSelect',
    'contributorCheckbox',
    'managerRetentionSwitch',
    'retentionWarning',
    'rateChangeNotice',
  ];

  static values = { savedRate: String, savedBasis: String };

  connect() {
    this.updateManagerLockState();
    this.updateManagerRetentionState();
    this.updateRateChangeNotice();
  }

  // Handler `input` / `change` posé sur le bloc Paramètres.
  updateRateChangeNotice() {
    if (!this.hasRateChangeNoticeTarget) return;

    const fields = this.element.elements;
    const changed = rateOrBasisChanged({
      savedRate: this.savedRateValue,
      savedBasis: this.savedBasisValue,
      rate: fields.namedItem(RATE_FIELD)?.value ?? '',
      basis: fields.namedItem(BASIS_FIELD)?.value ?? '',
    });
    this.rateChangeNoticeTarget.classList.toggle('hidden', !changed);
  }

  // Manager-select handler: force-check every contract owned by the picked
  // manager (sensible default — they administer the account, so all their
  // lots opt in by default), then recompute the lock state.
  onManagerChange() {
    if (!this.hasManagerSelectTarget) return;

    const managerId = this.managerSelectTarget.value;
    this.contributorCheckboxTargets.forEach((checkbox) => {
      if (this.#isManagerCheckbox(checkbox, managerId)) {
        checkbox.checked = true;
      }
    });
    this.updateManagerLockState();
    // Les chiffres de l'avertissement viennent du serveur et décrivent le
    // gestionnaire d'AVANT : rejouer l'état le fait disparaître ici même.
    this.updateManagerRetentionState();
  }

  // Re-evaluates the disabled state across every manager checkbox. The rule:
  // lock only when exactly one manager checkbox is still checked (the LAST
  // remaining one), otherwise leave them all enabled so the architect can
  // partial-uncheck N-1 of N (F7). Non-manager checkboxes are always enabled.
  updateManagerLockState() {
    if (!this.hasManagerSelectTarget) return;

    const managerId = this.managerSelectTarget.value;
    const managerCheckboxes = this.contributorCheckboxTargets.filter((checkbox) =>
      this.#isManagerCheckbox(checkbox, managerId)
    );
    const checkedCount = managerCheckboxes.filter((checkbox) => checkbox.checked).length;

    this.contributorCheckboxTargets.forEach((checkbox) => {
      const isManager = this.#isManagerCheckbox(checkbox, managerId);
      if (!isManager) {
        checkbox.disabled = false;
        this.#removeHiddenMirror(checkbox);
        return;
      }
      const shouldLock = checkedCount === 1 && checkbox.checked;
      checkbox.disabled = shouldLock;
      if (shouldLock) {
        this.#ensureHiddenMirror(checkbox);
      } else {
        this.#removeHiddenMirror(checkbox);
      }
    });
  }

  // Handler de l'interrupteur de déduction. L'avertissement n'existe dans le DOM
  // que si le gestionnaire a déjà été prélevé : il est rendu par le serveur avec
  // le compte et le montant réels, jamais fabriqué ici.
  onManagerRetentionChange() {
    this.updateManagerRetentionState();
  }

  updateManagerRetentionState() {
    if (!this.hasManagerRetentionSwitchTarget) return;

    const charged = this.managerRetentionSwitchTarget.checked;

    if (this.hasRetentionWarningTarget) {
      // Deux conditions, pas une. L'avertissement ne parle que si la déduction
      // est coupée ET si ses chiffres décrivent bien le gestionnaire sélectionné.
      // Le compte et le montant sont rendus UNE FOIS par le serveur, pour
      // l'entreprise nommée dans `data-contractor-id` ; changer de gestionnaire
      // dans le select ne les recalcule pas, et l'avertissement affirmait alors
      // un nombre de déductions à propos d'une entreprise qui n'en a aucune.
      // On préfère se taire : c'est un message consultatif, et le serveur refuse
      // de toute façon le changement dès qu'une contribution existe.
      this.retentionWarningTarget.classList.toggle(
        'hidden',
        charged || !this.#retentionWarningDescribesSelection()
      );
    }
  }

  // Vrai quand les chiffres rendus par le serveur portent sur le gestionnaire
  // actuellement sélectionné. Sans select dans le DOM, rien ne peut dériver et
  // l'avertissement reste valable.
  #retentionWarningDescribesSelection() {
    if (!this.hasManagerSelectTarget) return true;

    const described = this.retentionWarningTarget.dataset.contractorId;
    if (!described) return true;

    return described === this.managerSelectTarget.value;
  }

  #isManagerCheckbox(checkbox, managerId) {
    return managerId !== '' && checkbox.dataset.contractorId === managerId;
  }

  #ensureHiddenMirror(checkbox) {
    if (checkbox.nextElementSibling?.dataset?.[HIDDEN_MIRROR_FLAG] === checkbox.id) return;

    const mirror = document.createElement('input');
    mirror.type = 'hidden';
    mirror.name = HIDDEN_MIRROR_NAME;
    mirror.value = checkbox.value;
    mirror.dataset[HIDDEN_MIRROR_FLAG] = checkbox.id;
    checkbox.insertAdjacentElement('afterend', mirror);
  }

  #removeHiddenMirror(checkbox) {
    const sibling = checkbox.nextElementSibling;
    if (sibling?.dataset?.[HIDDEN_MIRROR_FLAG] === checkbox.id) {
      sibling.remove();
    }
  }
}
